import { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Bold, Italic, Heading2, List, ListOrdered, Link as LinkIcon, Eye, Edit3 } from "lucide-react";

/** Lightweight markdown editor with a small toolbar + live preview tab.
 *  Stores plain markdown. Renders with react-markdown. */
export default function MarkdownEditor({ value, onChange, rows = 8, placeholder, testId }) {
  const taRef = useRef(null);
  const [mode, setMode] = useState("edit"); // "edit" | "preview"

  const wrap = (before, after = before, fallback = "") => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end) || fallback;
    const next = value.substring(0, start) + before + selected + after + value.substring(end);
    onChange(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  };

  const prefixLines = (prefix) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.substring(0, start);
    const sel = value.substring(start, end) || "Elemento";
    const after = value.substring(end);
    const lined = sel
      .split("\n")
      .map((l, i) => (prefix === "1. " ? `${i + 1}. ${l}` : `${prefix}${l}`))
      .join("\n");
    onChange(before + lined + after);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start, start + lined.length);
    }, 0);
  };

  const insertLink = () => {
    const ta = taRef.current;
    if (!ta) return;
    const url = window.prompt("URL:");
    if (!url) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = value.substring(start, end) || "enlace";
    const md = `[${text}](${url})`;
    onChange(value.substring(0, start) + md + value.substring(end));
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        <div className="flex items-center gap-0.5">
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => wrap("**", "**", "texto")} title="Negrita (Ctrl+B)">
            <Bold className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => wrap("*", "*", "texto")} title="Cursiva (Ctrl+I)">
            <Italic className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => prefixLines("## ")} title="Encabezado">
            <Heading2 className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => prefixLines("- ")} title="Lista">
            <List className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => prefixLines("1. ")} title="Lista numerada">
            <ListOrdered className="w-3.5 h-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={insertLink} title="Insertar enlace">
            <LinkIcon className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={mode === "edit" ? "default" : "ghost"}
            size="sm"
            className={`h-7 px-2 text-xs ${mode === "edit" ? "bg-brand text-white hover:bg-brand-hover" : ""}`}
            onClick={() => setMode("edit")}
            data-testid={testId ? `${testId}-edit` : undefined}
          >
            <Edit3 className="w-3 h-3 mr-1" /> Editar
          </Button>
          <Button
            type="button"
            variant={mode === "preview" ? "default" : "ghost"}
            size="sm"
            className={`h-7 px-2 text-xs ${mode === "preview" ? "bg-brand text-white hover:bg-brand-hover" : ""}`}
            onClick={() => setMode("preview")}
            data-testid={testId ? `${testId}-preview` : undefined}
          >
            <Eye className="w-3 h-3 mr-1" /> Vista previa
          </Button>
        </div>
      </div>
      {mode === "edit" ? (
        <Textarea
          ref={taRef}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder || "Escribe aquí. Usa la barra para dar formato (negrita, cursiva, listas, enlaces)…"}
          className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-data text-sm leading-relaxed"
        />
      ) : (
        <div
          className="prose prose-sm max-w-none p-4 min-h-[160px] bg-white"
          data-testid={testId ? `${testId}-rendered` : undefined}
        >
          {value && value.trim() ? (
            <MarkdownView text={value} />
          ) : (
            <p className="text-sm text-gray-400 italic">— Sin contenido —</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Read-only markdown renderer with sensible defaults (links open in new tab,
 *  headings tinted brand-ink, lists with spacing). */
export function MarkdownView({ text }) {
  return (
    <ReactMarkdown
      components={{
        h1: ({ node, ...props }) => <h2 className="font-display text-xl text-brand-ink mt-3 mb-2" {...props} />,
        h2: ({ node, ...props }) => <h3 className="font-display text-lg text-brand-ink mt-3 mb-2" {...props} />,
        h3: ({ node, ...props }) => <h4 className="font-display text-base text-brand-ink mt-2 mb-1.5" {...props} />,
        p: ({ node, ...props }) => <p className="text-sm text-gray-700 my-2 leading-relaxed" {...props} />,
        ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 text-sm text-gray-700 space-y-1" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 text-sm text-gray-700 space-y-1" {...props} />,
        li: ({ node, ...props }) => <li {...props} />,
        a: ({ node, ...props }) => (
          <a className="text-brand underline hover:text-brand-hover" target="_blank" rel="noopener noreferrer" {...props} />
        ),
        strong: ({ node, ...props }) => <strong className="text-brand-ink font-semibold" {...props} />,
        em: ({ node, ...props }) => <em className="italic" {...props} />,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}
